/**
 * Les lignes d'une intervention, et la mise en page du planning pendant qu'on y travaille (ADR-0016) : tout ce qui se décide sans
 * navigateur.
 *
 * UNE liste, une seule : celle que le serveur rend dans le briefing (`lignesPrevues`) et que l'on réécrit TOUJOURS en entier
 * (`PATCH … { revision, lignes }`, clé absente = inchangé, `[]` = aucune ligne). Chaque geste — quantité, retrait, déplacement,
 * ajout — produit une NOUVELLE liste complète ; l'écran ne garde aucune copie à part, il relit le briefing après l'écriture.
 *
 * Le prix n'est JAMAIS saisi : il est LU dans le chiffrage et relié à la ligne par sa clé (`cle` = `lineKey`). Une pièce lue dans la
 * gamme et pas encore enregistrée n'a pas de clé : repli par POSITION parmi les pièces sans clé (le serveur chiffre les pièces dans
 * l'ordre des lignes prévues). Le forfait de la tournée est une ligne calculée ; le modifier enregistre une ligne `FLAT_FEE` qui
 * REMPLACE le calcul automatique (quantité 0 = « non facturé »). La main d'œuvre de l'équipe vient des heures et le déplacement
 * automatique du tarif du site : jamais modifiés ici.
 *
 * Les « EN PLUS » (règle de Benjamin, 2026-10-24) : une ligne enregistrée de main d'œuvre (`LABOR`, des heures en plus de celles de
 * l'équipe) ou de déplacement (`TRAVEL`, un passage de plus) est CHIFFRÉE par sa clé et S'AJOUTE au calcul automatique, jamais elle
 * ne le remplace. Elle reste dans le tableau principal, comme une pièce, avec sa quantité et sa croix.
 *
 * Compris ou à facturer : sous contrat annuel, la VISITE (pièces de la gamme, heures de l'équipe, déplacement automatique) est
 * chiffrée pour la rentabilité mais n'entre pas dans le total à facturer ; les « en plus » et le forfait sont toujours à facturer.
 * C'est le serveur qui le décide (`facturation` de chaque ligne) : ici on ne fait que le dire.
 */
import type {
  BriefingIntervention,
  ChiffrageInterventionVue,
  FacturationBon,
  InterventionPlanning,
  LigneChiffreeVue,
  LignePrevue,
  LigneSaisieIntervention,
} from '../api/client';
import { OPTIONS_QUANTITE, lireDecimal, valeurAffichee } from './bon-edition';
import { libelleGenre, peutDeplacer } from './planning-interventions';
import { resumerChiffrage, type ResumeChiffrage } from './tarifs-mo';

export type Liste = readonly LigneSaisieIntervention[];

export type Resultat = { readonly ok: true; readonly lignes: Liste; readonly change: boolean } | { readonly ok: false; readonly message: string };

/** Limites du serveur (analyseur du contenu) : on les dit avant l'envoi plutôt qu'après un refus. */
export const REFERENCE_MAX = 80;
export const DESIGNATION_MAX = 300;

export const PHRASE_PALIER_REMPLACE = 'Changer de palier remplace les pièces du palier ; tes pièces ajoutées restent.';
export const PHRASE_HORS_TARIF = 'Hors tarif : aucun prix ne sera calculé pour cette ligne.';

// ------------------------------------------------------------------------------------------------------------------ droits

/** Les lignes se modifient quand on gère le planning ET que l'intervention est ouverte (ni validée ni annulée). */
export function peutModifierLesLignes(i: Pick<InterventionPlanning, 'status'>, peutGerer: boolean): boolean {
  return peutDeplacer(i, peutGerer);
}

export type ColonneLignes = 'reference' | 'designation' | 'quantite' | 'prixUnitaire' | 'total' | 'actions';

/** Les colonnes du tableau : celles du prix n'existent que pour qui voit les prix, celle des gestes que pour qui écrit. */
export function colonnesTableau(avecPrix: boolean, ecriture: boolean): readonly ColonneLignes[] {
  return [
    'reference',
    'designation',
    'quantite',
    ...(avecPrix ? (['prixUnitaire', 'total'] as const) : []),
    ...(ecriture ? (['actions'] as const) : []),
  ];
}

// ------------------------------------------------------------------------------------------- liste envoyée au serveur

/** Une ligne du briefing, telle qu'on la réécrit : la clé garde l'identité de la ligne, l'origine (palier) se conserve. */
export function versLigneSaisie(l: LignePrevue): LigneSaisieIntervention {
  return {
    ...(l.lineKey !== null ? { lineKey: l.lineKey } : {}),
    kind: l.kind,
    reference: l.reference,
    designation: l.designation,
    quantity: l.quantite,
    unit: l.unite,
    note: l.note,
    ...(l.serialNumber !== undefined ? { serialNumber: l.serialNumber } : {}),
    ...(l.versedFrom !== null ? { versedFrom: l.versedFrom } : {}),
  };
}

/** La liste COMPLÈTE affichée, dans son ordre, prête à être réécrite. */
export function lignesSaisies(b: Pick<BriefingIntervention, 'lignesPrevues'>): Liste {
  return b.lignesPrevues.lignes.map(versLigneSaisie);
}

/** Le corps de la modification : la révision lue dans le briefing, la liste complète. Le seul corps que l'écran envoie. */
export function corpsDeModification(revision: number, lignes: Liste): { readonly revision: number; readonly lignes: Liste } {
  return { revision, lignes };
}

// ----------------------------------------------------------------------------------------------------------------- tableau

export interface PrixLigne {
  readonly prixUnitaire: string | null;
  readonly total: string | null;
  readonly raison: string | null;
  readonly compte: boolean;
  /** COMPRIS au contrat annuel (chiffré, jamais facturé) ou À FACTURER : décidé par le serveur. */
  readonly facturation: LigneChiffreeVue['facturation'];
}

export interface Pastille {
  readonly texte: string;
  readonly ton: 'palier' | 'ajoutee' | 'genre';
}

/**
 * « palier B » pour une pièce venue d'un palier, « ajoutée » pour une pièce saisie ; une ligne de main d'œuvre ou de déplacement
 * enregistrée est un « en plus » (elle s'ajoute au calcul) ; le genre pour les autres lignes.
 */
export function pastilleLigne(l: Pick<LignePrevue, 'kind' | 'versedFrom'>): Pastille {
  if (l.kind === 'LABOR' || l.kind === 'TRAVEL') return { texte: `${libelleGenre(l.kind)} en plus`, ton: 'genre' };
  if (l.kind !== 'PART') return { texte: libelleGenre(l.kind), ton: 'genre' };
  if (l.versedFrom !== null) return { texte: `palier ${l.versedFrom.levelCode}`, ton: 'palier' };
  return { texte: 'ajoutée', ton: 'ajoutee' };
}

export interface PastilleFacturation {
  readonly texte: 'compris au contrat' | 'à facturer';
  readonly ton: 'compris' | 'a-facturer';
}

export const PASTILLE_COMPRIS: PastilleFacturation = { texte: 'compris au contrat', ton: 'compris' };
export const PASTILLE_A_FACTURER: PastilleFacturation = { texte: 'à facturer', ton: 'a-facturer' };

/**
 * La pastille de facturation d'une ligne chiffrée : « compris au contrat » quand le serveur la dit comprise ; « à facturer », discrète,
 * seulement sur un bon sous contrat annuel (c'est là qu'il faut distinguer les « en plus ») ; rien sur un bon facturé à la visite,
 * où tout est à facturer. Aucun prix lu : aucune pastille.
 */
export function pastilleFacturation(prix: PrixLigne | null, facturationBon: FacturationBon | null): PastilleFacturation | null {
  if (prix === null) return null;
  if (prix.facturation === 'COMPRIS') return PASTILLE_COMPRIS;
  return facturationBon === 'CONTRACT' ? PASTILLE_A_FACTURER : null;
}

/**
 * Une ligne sans prix se dit en orange quand elle manque au total À FACTURER ; comprise au contrat (tarif non paramétré, par
 * exemple), elle n'y entre pas : elle se dit en gris, sans alarmer.
 */
export function raisonEnAttention(prix: PrixLigne): boolean {
  return prix.compte && prix.facturation !== 'COMPRIS';
}

export interface RangeeLigne {
  /** La position dans la liste COMPLÈTE : c'est elle qui désigne la ligne pour chaque geste. */
  readonly index: number;
  readonly ligne: LignePrevue;
  readonly pastille: Pastille;
  /** `null` = aucun prix lu (droit absent, chiffrage pas encore arrivé, ou ligne que le chiffrage ne connaît pas). */
  readonly prix: PrixLigne | null;
  readonly facturation: PastilleFacturation | null;
  readonly quantiteModifiable: boolean;
}

export type GenreCalcule = 'MAIN_D_OEUVRE' | 'DEPLACEMENT' | 'FORFAIT' | 'AUTRE';

export interface RangeeCalculee {
  readonly genre: GenreCalcule;
  /** Le `lineKey` de la ligne enregistrée (forfait qui remplace le calcul, ligne chiffrée sans rangée en face) ; `null` = calcul automatique. */
  readonly cle: string | null;
  readonly libelle: string;
  readonly jour: string | null;
  readonly detail: string | null;
  /** Texte de la quantité (« 1 h 45 », « 1 ») ; `quantiteBrute` est ce que l'on édite, au point décimal. */
  readonly quantite: string | null;
  readonly quantiteBrute: string | null;
  readonly unite: string | null;
  readonly prix: PrixLigne;
  readonly facturation: PastilleFacturation | null;
  readonly modifiable: boolean;
  /** Quantité 0 sur une ligne qui remplace le calcul : « non facturé ». */
  readonly nonFacture: boolean;
  /**
   * Le déplacement AUTOMATIQUE seulement (jamais un « en plus ») : `RETIRABLE` (une croix le retire de ce passage),
   * `RETIRE_PASSAGE` (« Remettre » le refacture), `RETIRE_TOURNEE` (retiré pour toute la tournée : se change sur la tournée),
   * `null` pour toute autre ligne (dont le déplacement porté par une autre intervention du passage).
   */
  readonly deplacement: EtatDeplacement;
}

export type EtatDeplacement = 'RETIRABLE' | 'RETIRE_PASSAGE' | 'RETIRE_TOURNEE' | null;

/** L'état du déplacement automatique d'une ligne de chiffrage (règle pure, testée). */
export function etatDeplacement(l: { readonly genre: string; readonly cle: string | null; readonly compte: boolean }, retire: ChiffrageInterventionVue['deplacementRetire']): EtatDeplacement {
  if (l.genre !== 'DEPLACEMENT' || l.cle !== null || !l.compte) return null;
  if (retire === 'TOURNEE') return 'RETIRE_TOURNEE';
  if (retire === 'INTERVENTION') return 'RETIRE_PASSAGE';
  return 'RETIRABLE';
}

export interface Tableau {
  readonly source: BriefingIntervention['lignesPrevues']['source'];
  /** Le choix du bon lu dans le chiffrage (`null` : pas de chiffrage lu, ou pas encore choisi). */
  readonly facturationBon: FacturationBon | null;
  /** La liste complète, base de tout geste. */
  readonly liste: Liste;
  readonly pieces: readonly RangeeLigne[];
  readonly calculees: readonly RangeeCalculee[];
  readonly resume: ResumeChiffrage | null;
}

const GENRES_CALCULES: readonly string[] = ['MAIN_D_OEUVRE', 'DEPLACEMENT', 'FORFAIT', 'AUTRE'];

function prixDe(c: ChiffrageInterventionVue['lignes'][number]): PrixLigne {
  return { prixUnitaire: c.prixUnitaire, total: c.total, raison: c.raison, compte: c.compte, facturation: c.facturation };
}

/**
 * Relie chaque ligne affichée à son prix : par la clé de la ligne enregistrée, sinon (pièce lue dans la gamme, pas encore
 * enregistrée) par POSITION parmi les pièces chiffrées sans clé. Le serveur chiffre les pièces dans l'ordre des lignes prévues.
 */
function prixParIndex(
  b: Pick<BriefingIntervention, 'lignesPrevues'>,
  c: ChiffrageInterventionVue,
): {
  readonly parIndex: Map<number, PrixLigne>;
  readonly clesLues: ReadonlySet<string>;
  readonly orphelines: readonly ChiffrageInterventionVue['lignes'][number][];
} {
  const parCle = new Map<string, ChiffrageInterventionVue['lignes'][number]>();
  const sansCle: ChiffrageInterventionVue['lignes'][number][] = [];
  for (const l of c.lignes) {
    if (l.cle !== null) {
      if (!parCle.has(l.cle)) parCle.set(l.cle, l);
    } else if (l.genre === 'PIECE') {
      sansCle.push(l);
    }
  }
  const resultat = new Map<number, PrixLigne>();
  const clesLues = new Set<string>();
  let rang = 0;
  b.lignesPrevues.lignes.forEach((l, index) => {
    if (l.lineKey !== null) {
      const p = parCle.get(l.lineKey);
      if (p !== undefined) {
        resultat.set(index, prixDe(p));
        clesLues.add(l.lineKey);
      }
    } else if (l.kind === 'PART') {
      const p = sansCle[rang];
      rang += 1;
      if (p !== undefined) resultat.set(index, prixDe(p));
    }
  });
  // Une pièce CHIFFRÉE qu'aucune ligne affichée ne porte (briefing et chiffrage lus à deux instants, ou ligne retirée entre les deux)
  // compte pourtant dans le total : elle est montrée plutôt que cachée.
  const orphelines = [
    ...c.lignes.filter((l) => l.genre === 'PIECE' && l.cle !== null && !clesLues.has(l.cle)),
    ...sansCle.slice(rang),
  ];
  return { parIndex: resultat, clesLues, orphelines };
}

/** Une quantité décimale en texte → nombre ; `null` = illisible ou absente. */
function nombre(q: string | null): number | null {
  if (q === null || q.trim() === '') return null;
  const n = Number(q);
  return Number.isFinite(n) ? n : null;
}

/**
 * Le tableau à montrer : les pièces et autres lignes enregistrées (avec leur prix quand on le voit), puis les lignes CALCULÉES
 * (main d'œuvre de l'équipe, déplacement automatique, forfait) tirées du chiffrage. Sans le droit des prix, aucun chiffrage n'est lu
 * et il n'y a que les lignes.
 *
 * Une ligne `FLAT_FEE` enregistrée dont le chiffrage porte la clé passe dans les lignes calculées (elle y remplace le forfait) ; tant
 * que le chiffrage n'est pas là, ou si aucune ligne de chiffrage ne la nomme, elle reste dans le tableau principal : jamais invisible.
 * Une ligne `LABOR` ou `TRAVEL` enregistrée est un « en plus » : elle reste dans le tableau principal avec SON prix (relié par sa clé),
 * et la ligne automatique (main d'œuvre de l'équipe, déplacement du site) reste en plus dans les lignes calculées.
 */
export function construireTableau(b: BriefingIntervention, chiffrage: ChiffrageInterventionVue | null, avecPrix: boolean): Tableau {
  const liste = lignesSaisies(b);
  const lu = avecPrix ? chiffrage : null;
  const facturationBon = lu?.facturationBon ?? null;
  const vide = { parIndex: new Map<number, PrixLigne>(), clesLues: new Set<string>(), orphelines: [] };
  const { parIndex: prix, clesLues, orphelines } = lu ? prixParIndex(b, lu) : vide;
  const clesForfait = new Set<string>();
  if (lu) for (const l of lu.lignes) if (l.cle !== null && l.genre === 'FORFAIT') clesForfait.add(l.cle);

  const pieces: RangeeLigne[] = [];
  /** Les clés dont le prix est déjà montré sur une rangée du tableau principal : leur ligne de chiffrage n'est pas répétée plus bas. */
  const clesMontrees = new Set<string>();
  b.lignesPrevues.lignes.forEach((ligne, index) => {
    if (ligne.lineKey !== null && clesForfait.has(ligne.lineKey) && ligne.kind === 'FLAT_FEE') return;
    const p = prix.get(index) ?? null;
    if (p !== null && ligne.lineKey !== null && clesLues.has(ligne.lineKey)) clesMontrees.add(ligne.lineKey);
    pieces.push({
      index,
      ligne,
      pastille: pastilleLigne(ligne),
      prix: p,
      facturation: pastilleFacturation(p, facturationBon),
      quantiteModifiable: true,
    });
  });

  const calculees: RangeeCalculee[] = [];
  if (lu) {
    for (const l of lu.lignes) {
      if (!GENRES_CALCULES.includes(l.genre)) continue;
      // Un « en plus » (main d'œuvre, déplacement) déjà montré avec sa rangée : une seule fois à l'écran.
      if (l.cle !== null && clesMontrees.has(l.cle)) continue;
      const genre = l.genre as GenreCalcule;
      // Seul le forfait se remplace : le déplacement automatique ne s'efface plus (une ligne de déplacement s'y AJOUTE).
      const remplacable = genre === 'FORFAIT' && l.compte;
      const p = prixDe(l);
      calculees.push({
        genre,
        cle: l.cle,
        libelle: l.libelle,
        jour: l.jour,
        detail: l.detail,
        quantite: l.quantite,
        quantiteBrute: l.quantite,
        unite: l.unite,
        prix: p,
        facturation: pastilleFacturation(p, facturationBon),
        modifiable: remplacable,
        nonFacture: remplacable && l.cle !== null && nombre(l.quantite) === 0,
        deplacement: etatDeplacement(l, lu.deplacementRetire ?? null),
      });
    }
  }
  // Les pièces chiffrées sans ligne en face : listées avec les autres lignes calculées (lecture seule), jamais comptées en silence.
  for (const l of orphelines) {
    const p = prixDe(l);
    calculees.push({
      genre: 'AUTRE',
      cle: l.cle,
      libelle: l.libelle,
      jour: l.jour,
      detail: l.detail,
      quantite: l.quantite,
      quantiteBrute: l.quantite,
      unite: l.unite,
      prix: p,
      facturation: pastilleFacturation(p, facturationBon),
      modifiable: false,
      nonFacture: false,
      deplacement: null,
    });
  }
  // Celles qui comptent d'abord ; celles que le passage porte ailleurs (grisées) en dernier, chacune dans l'ordre du chiffrage.
  const ordonnees = [...calculees.filter((r) => r.prix.compte), ...calculees.filter((r) => !r.prix.compte)];
  return { source: b.lignesPrevues.source, facturationBon, liste, pieces, calculees: ordonnees, resume: lu ? resumerChiffrage(lu) : null };
}

/** « 2 », « 2,5 l », « 1 334 » ; « à préciser » pour une quantité inconnue (jamais « 1 »). */
export function texteQuantite(quantite: string | null, unite: string | null): string {
  if (quantite === null) return 'à préciser';
  return `${valeurAffichee(quantite)}${unite ? ` ${unite}` : ''}`;
}

/** La quantité d'une ligne calculée qui ne se modifie pas : « 1 h 45 » pour la main d'œuvre (déjà écrite avec son unité), « 1 forfait » ailleurs. */
export function quantiteCalculee(r: Pick<RangeeCalculee, 'genre' | 'quantite' | 'unite'>): string {
  if (r.genre === 'MAIN_D_OEUVRE') return r.quantite ?? '—';
  const n = valeurAffichee(r.quantite);
  return `${n === '' ? '—' : n}${r.unite ? ` ${r.unite}` : ''}`;
}

// --------------------------------------------------------------------------------------------------------------- gestes

const MSG_QUANTITE = 'Indique une quantité.';

/** Les gestes qui n'ont rien à changer rendent la même liste, `change: false` : l'écran n'écrit pas. */
const inchange = (lignes: Liste): Resultat => ({ ok: true, lignes, change: false });

/** `quantite > 0` partout, sauf déplacement et forfait où 0 veut dire « non facturé ». */
export function zeroPermis(kind: LignePrevue['kind']): boolean {
  return kind === 'TRAVEL' || kind === 'FLAT_FEE';
}

function lireQuantite(texte: string, zero: boolean): { ok: true; valeur: string | null } | { ok: false; message: string } {
  const l = lireDecimal(texte, OPTIONS_QUANTITE);
  if (!l.ok) return { ok: false, message: l.erreur };
  if (l.vide) return { ok: true, valeur: null };
  if (Number(l.valeur) === 0 && !zero) return { ok: false, message: 'La quantité doit être supérieure à zéro : pour retirer la ligne, utilise la croix.' };
  return { ok: true, valeur: l.valeur };
}

function canonique(q: string | null): string | null {
  if (q === null) return null;
  const l = lireDecimal(q, OPTIONS_QUANTITE);
  return l.ok && !l.vide ? l.valeur : q;
}

/** Retire la ligne `index` de la liste complète. */
export function retirer(liste: Liste, index: number): Resultat {
  if (!Number.isInteger(index) || index < 0 || index >= liste.length) return { ok: false, message: 'Cette ligne n’existe plus.' };
  return { ok: true, change: true, lignes: liste.filter((_, n) => n !== index) };
}

/**
 * Monte (`-1`) ou descend (`+1`) la ligne `index` d'un cran PARMI LES LIGNES VISIBLES (`visibles` = leurs positions dans la liste
 * complète : les lignes enregistrées que le tableau range ailleurs ne comptent pas comme voisines).
 */
export function deplacer(liste: Liste, index: number, sens: -1 | 1, visibles?: readonly number[]): Resultat {
  if (!Number.isInteger(index) || index < 0 || index >= liste.length) return { ok: false, message: 'Cette ligne n’existe plus.' };
  const ordre = visibles ?? liste.map((_, n) => n);
  const rang = ordre.indexOf(index);
  const voisin = rang === -1 ? undefined : ordre[rang + sens];
  if (voisin === undefined) {
    return { ok: false, message: sens === -1 ? 'Cette ligne est déjà la première.' : 'Cette ligne est déjà la dernière.' };
  }
  const copie = [...liste];
  const [ligne] = copie.splice(index, 1);
  if (ligne === undefined) return { ok: false, message: 'Cette ligne n’existe plus.' };
  copie.splice(voisin, 0, ligne);
  return { ok: true, change: true, lignes: copie };
}

export function monter(liste: Liste, index: number, visibles?: readonly number[]): Resultat {
  return deplacer(liste, index, -1, visibles);
}

export function descendre(liste: Liste, index: number, visibles?: readonly number[]): Resultat {
  return deplacer(liste, index, 1, visibles);
}

/** Les flèches d'une ligne : la première ne monte pas, la dernière ne descend pas (parmi les lignes visibles). */
export function bornesLigne(rang: number, total: number): { readonly haut: boolean; readonly bas: boolean } {
  return { haut: rang > 0, bas: rang < total - 1 };
}

/**
 * La quantité de la ligne `index` modifiée à la française (« 2,5 » → « 2.5 »). Illisible : refusée avec son message. Identique à
 * l'existante, ou vide sur une quantité déjà inconnue : rien à écrire. Vide sur une quantité connue : refusée (on n'efface pas
 * un nombre sans le dire).
 */
export function changerQuantite(liste: Liste, index: number, texte: string): Resultat {
  const ligne = liste[index];
  if (ligne === undefined) return { ok: false, message: 'Cette ligne n’existe plus.' };
  // Une ligne de main d'œuvre enregistrée est un « en plus » (des heures au-delà de celles de l'équipe) : sa quantité se règle ici.
  const lue = lireQuantite(texte, zeroPermis(ligne.kind));
  if (!lue.ok) return lue;
  if (lue.valeur === null) return ligne.quantity === null ? inchange(liste) : { ok: false, message: MSG_QUANTITE };
  if (lue.valeur === canonique(ligne.quantity)) return inchange(liste);
  return { ok: true, change: true, lignes: liste.map((l, n) => (n === index ? { ...l, quantity: lue.valeur } : l)) };
}

/**
 * Les lignes d'une intervention qui vient d'être créée, avec les pièces en plus demandées à la création : les lignes prévues lues
 * (les pièces du palier, quand il y en a) PUIS les pièces ajoutées. Rien n'est jamais perdu : une liste enregistrée remplace la lecture
 * de la gamme, il faut donc y reporter ce que cette lecture donnait.
 */
export function listeAvecPiecesEnPlus(b: Pick<BriefingIntervention, 'lignesPrevues'>, extras: Liste): Liste {
  return [...lignesSaisies(b), ...extras];
}

export type ModeAjout = 'TARIF' | 'LIBRE';

export interface EntreeAjout {
  readonly mode: ModeAjout;
  readonly reference: string;
  readonly designation: string;
  readonly quantite: string;
}

export const AJOUT_VIDE: EntreeAjout = { mode: 'TARIF', reference: '', designation: '', quantite: '1' };

/**
 * Ajoute une pièce en fin de liste. Une pièce du TARIF exige sa référence (choisie dans la liste du tarif de la marque) ; une pièce
 * HORS TARIF peut s'en passer (texte libre, aucun prix ne sera calculé). La désignation est toujours obligatoire. La quantité est
 * positive (« 1 » proposé). Aucune clé : le serveur donne l'identifiant, aucun palier : c'est une pièce « ajoutée ».
 */
export function ajouter(liste: Liste, e: EntreeAjout): Resultat {
  const reference = e.reference.trim();
  const designation = e.designation.trim();
  if (e.mode === 'TARIF' && reference === '') {
    return { ok: false, message: 'Choisis la référence dans la liste du tarif ; si elle n’y est pas, utilise « Pièce hors tarif ».' };
  }
  if (reference.length > REFERENCE_MAX) return { ok: false, message: `La référence est trop longue (${REFERENCE_MAX} caractères au plus).` };
  if (designation === '') return { ok: false, message: 'Donne une désignation à la pièce.' };
  if (designation.length > DESIGNATION_MAX) return { ok: false, message: `La désignation est trop longue (${DESIGNATION_MAX} caractères au plus).` };
  const q = lireQuantite(e.quantite, false);
  if (!q.ok) return q;
  if (q.valeur === null) return { ok: false, message: MSG_QUANTITE };
  const ligne: LigneSaisieIntervention = {
    kind: 'PART',
    reference: reference === '' ? null : reference,
    designation,
    quantity: q.valeur,
    unit: null,
    note: null,
  };
  return { ok: true, change: true, lignes: [...liste, ligne] };
}

/**
 * La désignation après le choix d'une référence du tarif : celle du tarif se pose quand la case est vide ou ne contient que ce
 * que le tarif y avait posé ; un texte écrit à la main n'est jamais écrasé.
 */
export function designationApresChoix(actuelle: string, posee: string | null, description: string): string {
  if (actuelle.trim() === '' || (posee !== null && actuelle === posee)) return description;
  return actuelle;
}

export interface CibleCalculee {
  readonly genre: 'DEPLACEMENT' | 'FORFAIT';
  readonly cle: string | null;
  readonly libelle: string;
}

const KIND_CALCULE: Readonly<Record<CibleCalculee['genre'], 'TRAVEL' | 'FLAT_FEE'>> = { DEPLACEMENT: 'TRAVEL', FORFAIT: 'FLAT_FEE' };

/**
 * Règle la quantité d'une ligne calculée : une ligne enregistrée la change ; sans ligne, on en crée une. Pour le FORFAIT, la ligne
 * créée REMPLACE le calcul (0 = « non facturé ») ; pour le DÉPLACEMENT, elle s'AJOUTE au déplacement automatique (un passage de
 * plus, règle du 2026-10-24). Jamais de prix : le tarif reste celui du site ou de la tournée.
 */
export function fixerQuantiteCalculee(liste: Liste, cible: CibleCalculee, texte: string): Resultat {
  const lue = lireQuantite(texte, true);
  if (!lue.ok) return lue;
  if (lue.valeur === null) return { ok: false, message: MSG_QUANTITE };
  if (cible.cle !== null) {
    const index = liste.findIndex((l) => l.lineKey === cible.cle);
    if (index === -1) return { ok: false, message: 'Cette ligne n’existe plus.' };
    const actuelle = liste[index];
    if (actuelle !== undefined && lue.valeur === canonique(actuelle.quantity)) return inchange(liste);
    return { ok: true, change: true, lignes: liste.map((l, n) => (n === index ? { ...l, quantity: lue.valeur } : l)) };
  }
  const ligne: LigneSaisieIntervention = {
    kind: KIND_CALCULE[cible.genre],
    reference: null,
    designation: cible.libelle,
    quantity: lue.valeur,
    unit: null,
    note: null,
  };
  return { ok: true, change: true, lignes: [...liste, ligne] };
}

/** La croix du forfait : quantité 0, « non facturé » (la ligne reste, elle dit qu'on a choisi de ne pas facturer). */
export function retirerCalculee(liste: Liste, cible: CibleCalculee): Resultat {
  return fixerQuantiteCalculee(liste, cible, '0');
}

export const LIBELLE_DEPLACEMENT_EN_PLUS = 'Déplacement en plus';

/**
 * « + Déplacement en plus » : une ligne de déplacement de quantité 1, qui S'AJOUTE au déplacement automatique (toujours à facturer,
 * même sous contrat annuel). Elle apparaît ensuite dans le tableau, où sa quantité se change et sa croix la retire.
 */
export function ajouterDeplacementEnPlus(liste: Liste): Resultat {
  return fixerQuantiteCalculee(liste, { genre: 'DEPLACEMENT', cle: null, libelle: LIBELLE_DEPLACEMENT_EN_PLUS }, '1');
}

/** Une intervention d'atelier n'a pas de site, donc pas de déplacement : rien à ajouter. */
export function deplacementEnPlusPossible(i: { readonly site: unknown }): boolean {
  return i.site !== null;
}

// ---------------------------------------------------------------------------------------------------------------- phrases

export const PHRASE_NON_FACTURE = 'non facturé';

/** Pourquoi une ligne n'a pas de prix, quand le chiffrage la connaît mais ne l'a pas chiffrée. */
export function sansPrixLu(prix: PrixLigne | null): boolean {
  return prix !== null && prix.total === null;
}

// ----------------------------------------------------------------------------------------------------------- mise en page

/**
 * Le planning se réduit (un tiers) quand une intervention est ouverte : on n'affecte pas en lisant la semaine. « Agrandir » le
 * rend à sa largeur d'avant sans fermer l'intervention. Aucune intervention ouverte : la mise en page d'avant.
 */
export function classeMiseEnPage(interventionOuverte: boolean, agrandi: boolean): string {
  return interventionOuverte && !agrandi ? 'planning-mise-en-page planning-mise-en-page-detail' : 'planning-mise-en-page';
}

/** Le lien de bascule n'existe que s'il y a une intervention ouverte. */
export function basculePossible(interventionOuverte: boolean): boolean {
  return interventionOuverte;
}

export function libelleBascule(agrandi: boolean): string {
  return agrandi ? '↔ Réduire le planning' : '↔ Agrandir le planning';
}

export type EtatAAffecter = 'CHARGEMENT' | 'VIDE' | 'REMPLIE';

/** La liste « À affecter » : vide, elle se réduit à une ligne ; pleine, elle reste visible. `null` = pas encore lue. */
export function etatAAffecter(liste: { readonly items: readonly unknown[] } | null): EtatAAffecter {
  if (liste === null) return 'CHARGEMENT';
  return liste.items.length === 0 ? 'VIDE' : 'REMPLIE';
}

/**
 * Cette liste ne porte que les interventions SANS AUCUN JOUR ; le compteur « N à affecter » de l'en-tête compte aussi les jours sans équipe,
 * qui sont sur la ligne « À affecter » de la grille. La phrase le dit, sinon « Aucune intervention à affecter » contredit « 2 à affecter ».
 */
export const PHRASE_RIEN_A_AFFECTER = 'Aucune intervention sans jour travaillé. Celles qui ont un jour mais pas d’équipe sont sur la ligne « À affecter » de la grille.';
